import type { TimelineEntry, TimelineKind } from "./deriveTimelineEntries";

// Stored as midnight UTC with no meaningful time, so formatting them in the
// viewer's zone would show the previous day (or month) west of UTC.
const DATE_ONLY_KINDS: ReadonlySet<TimelineKind> = new Set<TimelineKind>([
  "custom",
  "resultsAnnouncement",
]);

/**
 * `viewerTimeZone` defaults to the runtime's zone; it is a parameter so the
 * west-of-UTC case can be tested without depending on the runner's zone.
 */
export function formatTimelineDate(
  entry: Pick<TimelineEntry, "date" | "showDay" | "kind">,
  viewerTimeZone?: string,
): string {
  return new Intl.DateTimeFormat("en-GB", {
    ...(entry.showDay && { day: "numeric" }),
    month: "long",
    year: "numeric",
    timeZone: DATE_ONLY_KINDS.has(entry.kind) ? "UTC" : viewerTimeZone,
  }).format(new Date(entry.date));
}

/**
 * A month-only entry stays upcoming until its month is over, so a row reading
 * "March 2027" isn't muted while March is still underway. Month-only entries
 * are date-only, so the month boundary is UTC, matching how they're formatted.
 */
export function isTimelineEntryPast(
  entry: Pick<TimelineEntry, "date" | "showDay">,
  now: Date = new Date(),
): boolean {
  const date = new Date(entry.date);
  if (entry.showDay) {
    return date < now;
  }
  const nextMonth = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
  return nextMonth <= now.getTime();
}
